import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, getRouteApi, Link } from '@tanstack/react-router'
import { ArrowLeft, Pencil, Plus, Trash2, Users } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { deleteMember } from '@/api/endpoints/members'
import { teamMemberQueries } from '@/api/endpoints/team-members'
import { teamQueries } from '@/api/endpoints/teams'
import type { Member } from '@/api/generated/types'
import { apiErrorMessage } from '@/api/errors'
import { ConfirmDialog } from '@/components/kit/confirm-dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TeamDisbandDialog } from '@/features/teams/team-disband-dialog'
import { TeamMemberDialog } from '@/features/teams/team-member-dialog'
import {
  dateOnly,
  invalidateTeamMembership,
  isActiveMember,
  memberProfileName,
} from '@/features/teams/team-member-form-schema'
import { memberRoleLabel } from '@/lib/member-roles'

const teamsRoute = getRouteApi('/_authed/teams')

export const Route = createFileRoute('/_authed/teams/$uuid_/members')({
  loader: ({ context, params }) =>
    Promise.all([
      context.queryClient.prefetchQuery(teamQueries.detail(params.uuid)),
      context.queryClient.prefetchQuery(teamMemberQueries.list(params.uuid)),
    ]),
  component: TeamMembersPage,
})

const dateOrDash = (value: string | null | undefined) => dateOnly(value) || '—'

function TeamMembersPage() {
  const { uuid } = Route.useParams()
  const listSearch = teamsRoute.useSearch()
  const queryClient = useQueryClient()
  const { data: team } = useQuery(teamQueries.detail(uuid))
  const { data: members, isLoading, isError, error } = useQuery(teamMemberQueries.list(uuid))
  const [editing, setEditing] = useState<Member | undefined>()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [removing, setRemoving] = useState<Member | null>(null)
  const [disbandOpen, setDisbandOpen] = useState(false)

  const openDialog = (member?: Member) => {
    setEditing(member)
    setDialogOpen(true)
  }

  const remove = async (member: Member) => {
    await deleteMember(member.uuid ?? '')
    await invalidateTeamMembership(queryClient, uuid)
    toast.success('Member removed')
    setRemoving(null)
  }

  const hasActive = (members ?? []).some(isActiveMember)

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/teams/$uuid" params={{ uuid }} search={listSearch}>
            <ArrowLeft />
            Team
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold">{team?.name ?? 'Team'} members</h1>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={() => openDialog()}>
            <Plus />
            Add
          </Button>
          <Button
            size="sm"
            variant="destructive"
            disabled={!hasActive}
            onClick={() => setDisbandOpen(true)}
          >
            <Users />
            Disband
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : isError ? (
            <p role="alert" className="text-sm text-destructive">
              {apiErrorMessage(error)}
            </p>
          ) : !members?.length ? (
            <p className="text-sm text-muted-foreground">This team has no members.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Profile</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Left</TableHead>
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.uuid}>
                    <TableCell className="font-medium">{memberProfileName(member)}</TableCell>
                    <TableCell>{memberRoleLabel(member.role)}</TableCell>
                    <TableCell>{dateOrDash(member.join_date)}</TableCell>
                    <TableCell>{dateOrDash(member.leave_date)}</TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label={`Edit ${memberProfileName(member)}`}
                          onClick={() => openDialog(member)}
                        >
                          <Pencil />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label={`Remove ${memberProfileName(member)}`}
                          onClick={() => setRemoving(member)}
                        >
                          <Trash2 />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <TeamMemberDialog
        teamUuid={uuid}
        member={editing}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
      <TeamDisbandDialog
        teamUuid={uuid}
        members={members ?? []}
        open={disbandOpen}
        onOpenChange={setDisbandOpen}
      />
      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title="Remove this member?"
        description={`This permanently deletes the membership of ${removing ? memberProfileName(removing) : ''}. To record a departure, edit the member and set a leave date instead.`}
        confirmLabel="Remove"
        destructive
        onConfirm={() => (removing ? remove(removing) : undefined)}
      />
    </div>
  )
}
